// Pre-load voices globally to ensure they are available when needed
const speechSupported = 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
let availableVoices = speechSupported ? window.speechSynthesis.getVoices() : [];
if (speechSupported) {
  window.speechSynthesis.onvoiceschanged = () => {
    availableVoices = window.speechSynthesis.getVoices();
  };
}

const initReadingMode = () => {
  const readBtn = document.getElementById('start-reading-mode');
  if (!readBtn) return;

  const contentContainer = document.getElementById('story-content');
  const readingModeContainer = document.getElementById('reading-mode-container');
  const readingText = document.getElementById('reading-text');
  const exitBtn = document.getElementById('exit-reading-mode');
  const pauseBtn = document.getElementById('pause-reading-mode');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  
  // Prevent re-initialization if already set up
  if (readBtn.hasAttribute('data-initialized')) return;
  readBtn.setAttribute('data-initialized', 'true');

  // Browsers without speech synthesis keep the normal text and say so, instead of hiding the story.
  if (!speechSupported) {
    readBtn.disabled = true;
    readBtn.classList.add('opacity-60', 'cursor-not-allowed');
    readBtn.querySelector('span').textContent = 'Reading mode needs a browser with speech support';
    return;
  }

  let synth = window.speechSynthesis;
  let utterance = null;
  let words = [];
  let charToWordIndex = [];

  readBtn.addEventListener('click', (e) => {
    e.preventDefault();
    
    // Hide normal content, show reading mode
    contentContainer.classList.add('hidden');
    readingModeContainer.classList.remove('hidden');
    
    // Plain text from the server; normalize whitespace so charIndex maps perfectly
    const rawText = readingText.getAttribute('data-raw-text') || '';
    const text = rawText.replace(/\s+/g, ' ').trim();

    // Split text into words and wrap in spans (text nodes only, never HTML)
    words = text.split(' ').filter(w => w.length > 0);
    readingText.textContent = '';
    words.forEach((word, i) => {
      const span = document.createElement('span');
      span.id = `word-${i}`;
      span.className = 'reading-word inline-block transition-colors duration-150 ease-in-out px-1 rounded';
      span.textContent = word;
      readingText.appendChild(span);
      readingText.appendChild(document.createTextNode(' '));
    });
    if (pauseBtn) {
      pauseBtn.textContent = 'Pause';
      pauseBtn.setAttribute('aria-pressed', 'false');
    }
    document.getElementById('reading-mode-heading')?.focus({ preventScroll: true });

    // Prepare speech synthesis
    utterance = new SpeechSynthesisUtterance(text);
    
    // Adjust speed and pitch for a friendly, storytelling tone
    utterance.rate = 0.9; 
    utterance.pitch = 1.2; 
    
    // Function to find the most natural, non-robotic voice available
    const getBestVoice = () => {
      // Some browsers need a moment to load voices. If empty, try again.
      if (availableVoices.length === 0) {
        availableVoices = synth.getVoices();
      }
      
      if (availableVoices.length === 0) {
        console.warn("Voices not loaded yet, falling back to default.");
        return null;
      }

      // Priority list of friendly, natural-sounding voices (mix of macOS, iOS, and Chrome specific)
      const preferredNames = [
        'Google US English',           // Chrome's high-quality cloud voice
        'Google UK English Female',
        'Ava',                         // Newer macOS/iOS premium voice
        'Samantha',                    // macOS classic natural voice
        'Alex',                        // macOS classic
        'Allison',
        'Susan',
        'Zoe',
        'Karen',
        'Daniel'
      ];
      
      for (const name of preferredNames) {
        const found = availableVoices.find(v => v.name.includes(name));
        if (found) return found;
      }
      
      // Fallback: any English voice that might be female/friendly
      return availableVoices.find(v => v.lang.startsWith('en') && (v.name.includes('Female') || v.name.includes('woman'))) 
             || availableVoices.find(v => v.lang.startsWith('en')) 
             || availableVoices[0];
    };

    const bestVoice = getBestVoice();
    if (bestVoice) {
      utterance.voice = bestVoice;
    }

    // Build the character index mapping to words
    charToWordIndex = [];
    let currentCharIndex = 0;
    for (let i = 0; i < words.length; i++) {
      let start = currentCharIndex;
      let end = currentCharIndex + words[i].length;
      charToWordIndex.push({ start, end, index: i });
      currentCharIndex += words[i].length + 1; // +1 for the space
    }

    // Handle word boundary event
    utterance.onboundary = (event) => {
      if (event.name === 'word') {
        // Because of variations in browser APIs, we allow a slight margin of error in charIndex matching
        const wordInfo = charToWordIndex.find(w => event.charIndex >= w.start && event.charIndex <= w.end + 1);
        
        if (wordInfo) {
          // Reset all words
          document.querySelectorAll('.reading-word').forEach(el => {
            el.classList.remove('text-blue', 'bg-blue/10', 'font-bold');
            el.classList.add('text-ink');
          });
          
          // Highlight current word with book-like styling
          const currentWordEl = document.getElementById(`word-${wordInfo.index}`);
          if (currentWordEl) {
            currentWordEl.classList.remove('text-ink');
            currentWordEl.classList.add('text-blue', 'bg-blue/10', 'font-bold');
            
            // Scroll into view if needed (smoothly, but only if it's far down)
            const rect = currentWordEl.getBoundingClientRect();
            if (rect.top > window.innerHeight * 0.7 || rect.top < 0) {
              currentWordEl.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
            }
          }
        }
      }
    };

    utterance.onend = () => {
      // Clean up when finished
      document.querySelectorAll('.reading-word').forEach(el => {
        el.classList.remove('text-blue', 'bg-blue/10', 'font-bold');
        el.classList.add('text-ink');
      });
    };

    // Make sure we aren't already speaking
    synth.cancel();
    synth.speak(utterance);
  });

  exitBtn.addEventListener('click', () => {
    synth.cancel(); // Stop reading
    readingModeContainer.classList.add('hidden');
    contentContainer.classList.remove('hidden');
    readBtn.focus();
  });

  if (pauseBtn) {
    pauseBtn.addEventListener('click', () => {
      const pausing = pauseBtn.getAttribute('aria-pressed') !== 'true';
      if (pausing) { synth.pause(); } else { synth.resume(); }
      pauseBtn.setAttribute('aria-pressed', String(pausing));
      pauseBtn.textContent = pausing ? 'Resume' : 'Pause';
    });
  }

  // Leaving the page must not keep talking
  window.addEventListener('pagehide', () => synth.cancel());
  document.addEventListener('turbo:before-visit', () => synth.cancel());
};

// Initialize on DOMContentLoaded and Turbo Load
document.addEventListener('DOMContentLoaded', initReadingMode);
document.addEventListener('turbo:load', initReadingMode);
